import { useThemeContext } from "@tokenterminal/core/ui/theme/ThemeContext"
import { type ReactNode, useEffect, useRef, useState } from "react"

// Interactive diagrams for the engineering posts, used by name from the MDX. MarkdownContentRenderer
// loads this module with next/dynamic, so only the posts that use a diagram download it.

/** Returns true when the resolved theme is dark. Falls back to dark if context unavailable. */
const useIsDark = () => {
  try {
    // biome-ignore lint/correctness/useHookAtTopLevel: intentional try/catch to allow rendering outside a ThemeProvider
    const { theme } = useThemeContext()
    return theme !== "light"
  } catch {
    return true
  }
}

/** Coverage status dot. */
const StatusDot = ({ status }: { status: "full" | "partial" | "missing" }) => {
  const color =
    status === "full"
      ? "bg-emerald-500"
      : status === "partial"
        ? "bg-amber-500"
        : "bg-neutral-400 dark:bg-neutral-700"
  return <span className={`w-1.5 h-1.5 rounded-full ${color} shrink-0 mt-[5px]`} />
}

/** Viz 1: A single skill — the atomic unit. Shows what a SKILL.md file looks like. */
export const SkillCard = () => (
  <div className="my-6 md:my-8">
    <div className="rounded-lg border border-border-default bg-bg-popover p-4">
      <div className="text-xs text-fg-secondary font-mono mb-3 opacity-50">
        .claude/skills/decode-contract/SKILL.md
      </div>
      <div className="text-sm font-medium text-fg-default mb-1">decode-contract</div>
      <div className="text-xs text-fg-secondary leading-relaxed mb-3">
        Decodes onchain events end-to-end. From contract address to generated SQL decoder models.
        Handles EVM (proxy detection, ABI fetching) and non-EVM (Algorand, Aptos, Cosmos).
      </div>
      <div className="text-xs text-fg-secondary font-mono mb-3 bg-neutral-100 dark:bg-neutral-900/50 rounded-sm px-2.5 py-1.5">
        /decode-contract uniswap arbitrum 0x1F98...F984
      </div>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {["Discovery", "Generation", "Validation", "Cost estimation"].map((phase, i) => (
          <span key={phase} className="flex items-center gap-1.5 text-xs text-fg-secondary">
            <span className="font-mono">{phase}</span>
            {i < 3 && <span className="opacity-30">→</span>}
          </span>
        ))}
      </div>
    </div>
  </div>
)

/** Viz 2: One capability expanded — shows how skills compose into a step. */
export const CapabilityDetail = () => (
  <div className="my-6 md:my-8">
    <div className="rounded-lg border border-border-default bg-bg-popover p-4">
      <div className="flex items-start gap-2 mb-3">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0 mt-[5px]" />
        <span className="text-xs text-fg-default leading-tight">
          Normalize raw blockchain data into queryable tables
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5 ml-3.5">
        {["research-chain", "scrape-chain", "validate-chain", "deploy-scraper"].map((name) => (
          <span
            key={name}
            className="inline-block text-xs leading-tight px-2 py-0.5 rounded-md bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-mono"
          >
            {name}
          </span>
        ))}
      </div>
    </div>
  </div>
)

/** Viz 3: Full capability graph — three layers with all tasks. */
export const CapabilityGraph = () => {
  type Task = { name: string; status: "full" | "partial" | "missing" }

  const layers: { name: string; tasks: Task[] }[] = [
    {
      name: "Blockchain integration",
      tasks: [
        {
          name: "Normalize raw blockchain data into queryable tables",
          status: "full",
        },
        { name: "Build data freshness alerting", status: "full" },
        {
          name: "Decode smart contract events into readable tables",
          status: "full",
        },
        {
          name: "Build cross-chain abstractions for token transfers and flows",
          status: "full",
        },
        {
          name: "Read onchain contract state for balances and supplies",
          status: "partial",
        },
        {
          name: "Calculate financial metrics from decoded data",
          status: "full",
        },
        {
          name: "Validate metrics against external benchmarks",
          status: "partial",
        },
        {
          name: "Run end-to-end integration tests automatically",
          status: "missing",
        },
        { name: "Confirm data accuracy with the project team", status: "full" },
      ],
    },
    {
      name: "Metric definition",
      tasks: [
        { name: "Research the traditional finance equivalent", status: "full" },
        { name: "Map the concept to onchain mechanics", status: "full" },
        { name: "Define the target metric specification", status: "full" },
        {
          name: "Validate against top projects in each sector",
          status: "full",
        },
        {
          name: "Check if the metric follows expected distributions",
          status: "full",
        },
        {
          name: "Connect the definition to concrete onchain events",
          status: "partial",
        },
        {
          name: "Generate methodology documentation automatically",
          status: "missing",
        },
      ],
    },
    {
      name: "App integration",
      tasks: [
        {
          name: "Define which metrics to build for this protocol",
          status: "full",
        },
        { name: "Write the methodology for each metric", status: "partial" },
        {
          name: "Research the protocol's business model and fee structure",
          status: "full",
        },
        {
          name: "Identify which smart contracts implement the business logic",
          status: "full",
        },
        { name: "Map contract events to financial metrics", status: "full" },
        {
          name: "Determine how onchain data is stored and accessed",
          status: "full",
        },
        {
          name: "Write SQL models that extract data from decoded events",
          status: "full",
        },
        {
          name: "Aggregate extracted data into standardized metrics",
          status: "full",
        },
        {
          name: "Compare results against the target specification",
          status: "full",
        },
        { name: "Validate against external benchmarks", status: "partial" },
        {
          name: "Review with the protocol team for accuracy",
          status: "partial",
        },
      ],
    },
  ]

  const covered = (tasks: Task[]) => tasks.filter((t) => t.status === "full").length

  const renderLayer = (layer: (typeof layers)[0]) => (
    <div key={layer.name} className="rounded-lg border border-border-default bg-bg-popover p-4">
      <div className="flex justify-between items-baseline mb-3">
        <span className="text-sm font-medium text-fg-default">{layer.name}</span>
        <span className="text-xs text-fg-secondary tabular-nums">
          {covered(layer.tasks)} / {layer.tasks.length}
        </span>
      </div>
      <div className="flex flex-col gap-2">
        {layer.tasks.map((task) => (
          <div key={task.name} className="flex items-start gap-2">
            <StatusDot status={task.status} />
            <span className="text-xs text-fg-secondary leading-tight">{task.name}</span>
          </div>
        ))}
      </div>
    </div>
  )

  return (
    <div className="my-6 md:my-8">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">{layers.map(renderLayer)}</div>
      <div className="flex justify-center gap-4 mt-4 text-xs text-fg-secondary">
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-emerald-500" />
          Covered
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-amber-500" />
          Partial
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-neutral-400 dark:bg-neutral-700" />
          Missing
        </span>
      </div>
    </div>
  )
}

/** Viz 4: Priority list — what to build next, derived from agent session heuristics. */
export const PriorityList = () => {
  type Priority = {
    level: string
    color: string
    items: { name: string; pill?: boolean }[]
  }

  const priorities: Priority[] = [
    {
      level: "P0 — build now",
      color: "text-red-600 dark:text-red-400",
      items: [
        { name: "validate-integration", pill: true },
        { name: "read-state", pill: true },
      ],
    },
    {
      level: "P1 — build soon",
      color: "text-amber-400",
      items: [
        { name: "validate-partner-data", pill: true },
        { name: "develop-api", pill: true },
        { name: "develop-mcp", pill: true },
      ],
    },
    {
      level: "P2 — backlog",
      color: "text-fg-secondary",
      items: [
        { name: "build-sheets" },
        { name: "serve-queries" },
        { name: "automate-hr" },
        { name: "automate-finance" },
      ],
    },
    {
      level: "Extensions",
      color: "text-fg-secondary",
      items: [
        { name: "integrate-chain +read-state" },
        { name: "integrate-app +validate-integration" },
        { name: "fill-gaps +add-ranges" },
      ],
    },
  ]

  return (
    <div className="my-6 md:my-8">
      <div className="rounded-lg border border-border-default bg-bg-popover p-4 md:p-5">
        <div className="text-xs font-medium text-fg-default mb-4">Priority — What to build</div>
        <div className="flex flex-col gap-3">
          {priorities.map((p) => (
            <div key={p.level} className="flex items-baseline gap-4">
              <span className={`text-xs font-medium ${p.color} w-28 shrink-0`}>{p.level}</span>
              <div className="flex flex-wrap gap-1.5">
                {p.items.map((item) =>
                  item.pill ? (
                    <span
                      key={item.name}
                      className={`inline-block text-xs leading-tight px-2 py-0.5 rounded-md font-mono ${
                        p.level.includes("P0")
                          ? "bg-red-500/10 text-red-600 dark:text-red-400"
                          : "bg-amber-500/10 text-amber-600 dark:text-amber-400"
                      }`}
                    >
                      {item.name}
                    </span>
                  ) : (
                    <span key={item.name} className="text-xs text-fg-secondary font-mono">
                      {item.name}
                      {p.items.indexOf(item) < p.items.length - 1 ? (
                        <span className="mx-1 opacity-30">·</span>
                      ) : null}
                    </span>
                  ),
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/** CX Agent — Pipeline dependency tracer (VISUAL 1). */
export const ClassifyTrace = () => {
  type Layer = "metric" | "intermediate" | "fact" | "source"
  const layerStyle: Record<
    Layer,
    { dot: string; bg: string; text: string; border: string; label: string }
  > = {
    metric: {
      dot: "bg-emerald-500",
      bg: "bg-emerald-500/10",
      text: "text-emerald-600 dark:text-emerald-400",
      border: "border-emerald-500/25",
      label: "Metric",
    },
    intermediate: {
      dot: "bg-violet-500",
      bg: "bg-violet-500/10",
      text: "text-violet-600 dark:text-violet-400",
      border: "border-violet-500/25",
      label: "Intermediate",
    },
    fact: {
      dot: "bg-blue-400",
      bg: "bg-blue-400/10",
      text: "text-blue-400",
      border: "border-blue-400/25",
      label: "Fact",
    },
    source: {
      dot: "bg-amber-500",
      bg: "bg-amber-500/10",
      text: "text-amber-500",
      border: "border-amber-500/25",
      label: "Source",
    },
  }
  const Node = ({
    layer,
    name,
    note,
    children,
  }: {
    layer: Layer
    name: string
    note?: string
    children?: ReactNode
  }) => {
    const s = layerStyle[layer]
    return (
      <div className="py-1.5">
        <div
          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border ${note ? "border-emerald-500/30 bg-emerald-500/5" : "border-border-default bg-neutral-100/60 dark:bg-neutral-900/60"}`}
        >
          <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${s.dot}`} />
          <span className="text-[12px] font-mono font-medium text-fg-default">{name}</span>
          <span
            className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${s.bg} ${s.text} ${s.border}`}
          >
            {s.label}
          </span>
          {note && (
            <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold">
              {note}
            </span>
          )}
        </div>
        {children && <div className="ml-6 pl-4 border-l border-border-default">{children}</div>}
      </div>
    )
  }
  return (
    <div className="my-6 md:my-8 rounded-xl border border-border-default bg-bg-popover p-6 overflow-x-auto shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none">
      <div className="flex items-center gap-3 mb-5 pb-3 border-b border-border-default">
        <div className="w-8 h-8 bg-emerald-500/10 border border-emerald-500/25 rounded-md flex items-center justify-center text-base">
          ◇
        </div>
        <div>
          <div className="text-[13px] font-semibold">near_metrics_revenue</div>
          <div className="text-xs text-fg-secondary font-mono">
            tt-business-intelligence.project_near
          </div>
        </div>
        <div className="flex gap-5 ml-auto">
          {[
            { val: "7", lbl: "Models" },
            { val: "6", lbl: "Sources" },
            { val: "2", lbl: "Depth" },
          ].map(({ val, lbl }) => (
            <div key={lbl} className="text-center">
              <div className="text-lg font-semibold">{val}</div>
              <div className="text-[10px] text-fg-secondary uppercase tracking-wider">{lbl}</div>
            </div>
          ))}
        </div>
      </div>
      <Node layer="metric" name="near_metrics_revenue">
        <Node layer="intermediate" name="int_near_gas_revenue_per_day">
          <Node layer="fact" name="fct_near_transactions">
            <Node layer="source" name="tt-blockchain.near.transactions" />
          </Node>
          <Node layer="fact" name="fct_near_receipts">
            <Node layer="source" name="tt-blockchain.near.receipts" />
          </Node>
        </Node>
        <Node layer="intermediate" name="int_near_intents_revenue" note="← source of issue">
          <Node layer="fact" name="fct_near_intents_treasury_balances" note="new model">
            <Node layer="source" name="tt-blockchain.near.account_changes" />
          </Node>
        </Node>
        <Node layer="fact" name="fct_near_near_prices">
          <Node
            layer="source"
            name="tt-abstractions.coingecko_private.coingecko_daily_market_data"
          />
        </Node>
      </Node>
      <div className="flex gap-4 mt-4 pt-4 border-t border-border-default flex-wrap">
        {[
          { dot: "bg-emerald-500", label: "Metric" },
          { dot: "bg-violet-500", label: "Intermediate" },
          { dot: "bg-blue-400", label: "Fact" },
          { dot: "bg-amber-500", label: "Source" },
        ].map(({ dot, label }) => (
          <div key={label} className="flex items-center gap-1.5 text-xs text-fg-secondary">
            <div className={`w-1.5 h-1.5 rounded-full ${dot}`} />
            {label}
          </div>
        ))}
      </div>
    </div>
  )
}

/** CX Agent — Investigation timeline (VISUAL 3). */
export const InvestigateFix = () => {
  const Code = ({ children }: { children: string }) => (
    <code className="font-mono text-[10px] bg-neutral-800 px-1 py-0.5 rounded-sm text-emerald-600 dark:text-emerald-400">
      {children}
    </code>
  )
  const events: Array<{
    time: string
    dot: string
    title: string
    desc: ReactNode
  }> = [
    {
      time: "00:00",
      dot: "bg-amber-500",
      title: "Ticket arrives",
      desc: "Client reports NEAR revenue shows $0 for the Intents product. External benchmark disagrees.",
    },
    {
      time: "00:02",
      dot: "bg-violet-500",
      title: "Classified",
      desc: "Data discrepancy → revenue metric → NEAR Intents product. Routed to autonomous investigation.",
    },
    {
      time: "00:05",
      dot: "bg-blue-400",
      title: "Pipeline traced",
      desc: (
        <span>
          Maps <Code>near_metrics_revenue</Code> → 7 models, 6 sources, depth 2. Identifies{" "}
          <Code>int_near_intents_revenue</Code> as the suspect.
        </span>
      ),
    },
    {
      time: "00:08",
      dot: "bg-emerald-500",
      title: "Registry checked",
      desc: (
        <span>
          NEAR Intents has two treasury accounts: <Code>1csfundsadmin.sputnik-dao.near</Code> and{" "}
          <Code>fefundsadmin.sputnik-dao.near</Code>
        </span>
      ),
    },
    {
      time: "00:12",
      dot: "bg-blue-400",
      title: "Queried warehouse",
      desc: (
        <span>
          Treasury balance deltas from <Code>tt-blockchain.near.account_changes</Code>. Daily
          inflow: ~$3.4K/day.
        </span>
      ),
    },
    {
      time: "00:18",
      dot: "bg-emerald-500",
      title: "Verified onchain",
      desc: "Cross-referenced with revenue.near.org API. Exact match on 7 consecutive days.",
    },
    {
      time: "00:25",
      dot: "bg-emerald-500",
      title: "Fix written",
      desc: (
        <span>
          New model <Code>fct_near_intents_treasury_balances</Code>. Rewired intermediate model to
          read treasury balance deltas.
        </span>
      ),
    },
    {
      time: "00:28",
      dot: "bg-emerald-500",
      title: "PR opened, findings posted",
      desc: "PR #23093 with 3 new models. Full investigation posted to ticket.",
    },
  ]
  return (
    <div className="my-6 md:my-8 rounded-xl border border-border-default bg-bg-popover p-6 shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none">
      <div className="flex items-center justify-between mb-5 pb-3 border-b border-border-default">
        <div className="text-[13px] font-semibold">CX-1409 · NEAR Intents revenue</div>
        <span className="text-xs font-medium px-2.5 py-1 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/25 rounded-sm font-mono">
          28 min total
        </span>
      </div>
      <div>
        {events.map((e, i) => (
          <div key={i} className="flex gap-3.5 pb-5 last:pb-0">
            <div className="flex flex-col items-center w-12 shrink-0">
              <span className="text-xs font-mono text-fg-secondary mb-1.5">{e.time}</span>
              <div className={`w-2 h-2 rounded-full shrink-0 z-10 ${e.dot}`} />
              {i < events.length - 1 && (
                <div className="w-px flex-1 bg-neutral-200 dark:bg-neutral-800 min-h-3 mt-1" />
              )}
            </div>
            <div>
              <div className="text-xs font-semibold mb-0.5">{e.title}</div>
              <div className="text-xs text-fg-secondary leading-relaxed">{e.desc}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/** Hero visual: grid of dots with an accent trace path representing data investigation at scale. */
export const DataCorrectnessHero = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const W = 1800,
      H = 945
    canvas.width = W
    canvas.height = H

    // Dark navy/charcoal background — matches other engineering hero images
    ctx.fillStyle = "#0d0f14"
    ctx.fillRect(0, 0, W, H)

    const cols = 48,
      rows = 25
    const sX = W / (cols + 1),
      sY = H / (rows + 1)
    const baseR = 2.5

    // Pipeline dependency tree: root → L1 (3) → L2 (varies) → L3 deep on left branch
    const nodes: [number, number, number][] = [
      [24, 4, 0], // 0: root
      [12, 9, 1],
      [24, 9, 1],
      [36, 9, 1], // 1-3: L1
      [5, 15, 2],
      [16, 15, 2], // 4-5: left L2
      [20, 15, 2],
      [27, 15, 2], // 6-7: center L2
      [30, 15, 2],
      [37, 15, 2],
      [44, 15, 2], // 8-10: right L2 (3 leaves)
      [11, 21, 3],
      [20, 21, 3], // 11-12: deep leaves
    ]
    const edges: [number, number][] = [
      [0, 1],
      [0, 2],
      [0, 3],
      [1, 4],
      [1, 5],
      [2, 6],
      [2, 7],
      [3, 8],
      [3, 9],
      [3, 10],
      [5, 11],
      [5, 12],
    ]
    // Green trace: root → left L1 → right L2 of left branch → bottom-right deep leaf
    const tracePath = [0, 1, 5, 12]
    const traceSet = new Set(tracePath)

    const nMap = new Map(nodes.map((n) => [`${n[0]},${n[1]}`, n]))
    const _nIdxMap = new Map(nodes.map((n, i) => [`${n[0]},${n[1]}`, i]))
    const nPx = nodes.map((n) => ({
      x: sX * (n[0] + 1),
      y: sY * (n[1] + 1),
      lv: n[2],
    }))
    const aByLevel = [0.85, 0.7, 0.55, 0.45]
    const rByLevel = [4, 3, 2.5, 2]
    const nR = (i: number) => baseR + rByLevel[nodes[i][2]]

    function distToAny(px: number, py: number) {
      let min = Infinity
      for (const n of nPx) min = Math.min(min, Math.sqrt((px - n.x) ** 2 + (py - n.y) ** 2))
      return min
    }

    // 1. Background grid dots only (tree nodes drawn last, on top of lines)
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const px = sX * (col + 1),
          py = sY * (row + 1)
        const key = `${col},${row}`
        if (!nMap.has(key)) {
          const d = distToAny(px, py)
          const t = Math.max(0, 1 - d / (sX * 3))
          ctx.beginPath()
          ctx.arc(px, py, baseR + t * 0.6, 0, Math.PI * 2)
          ctx.fillStyle = `rgba(255,255,255,${0.04 + t * t * 0.1})`
          ctx.fill()
        }
      }
    }

    // 2. White bus tree — high opacity covers background dots, no knockout needed
    const childrenOf = new Map<number, number[]>()
    for (const [ai, bi] of edges) {
      if (!childrenOf.has(ai)) childrenOf.set(ai, [])
      childrenOf.get(ai)!.push(bi)
    }
    ctx.lineJoin = "round"
    ctx.lineCap = "round"
    ctx.lineWidth = 2.5
    ctx.strokeStyle = "rgba(255,255,255,0.70)"
    for (const [pi, cs] of childrenOf) {
      const p = nPx[pi],
        pR = nR(pi)
      const branchY = p.y + (nPx[cs[0]].y - p.y) * 0.5
      const xs = cs.map((ci) => nPx[ci].x)
      ctx.beginPath()
      ctx.moveTo(p.x, p.y + pR)
      ctx.lineTo(p.x, branchY)
      ctx.stroke()
      ctx.beginPath()
      ctx.moveTo(Math.min(...xs), branchY)
      ctx.lineTo(Math.max(...xs), branchY)
      ctx.stroke()
      for (const ci of cs) {
        const cR = nR(ci)
        ctx.beginPath()
        ctx.moveTo(nPx[ci].x, branchY)
        ctx.lineTo(nPx[ci].x, nPx[ci].y - cR)
        ctx.stroke()
      }
    }

    // 3. Green trace — single continuous elbow path per leg (no corner gaps)
    ctx.lineWidth = 3
    ctx.strokeStyle = "rgba(0,207,157,0.95)"
    for (let i = 0; i < tracePath.length - 1; i++) {
      const ai = tracePath[i],
        bi = tracePath[i + 1]
      const a = nPx[ai],
        b = nPx[bi]
      const my = a.y + (b.y - a.y) * 0.5
      ctx.beginPath()
      ctx.moveTo(a.x, a.y + nR(ai))
      ctx.lineTo(a.x, my)
      ctx.lineTo(b.x, my)
      ctx.lineTo(b.x, b.y - nR(bi))
      ctx.stroke()
    }

    // 4. Tree nodes drawn on top of lines
    for (let i = 0; i < nodes.length; i++) {
      const n = nodes[i],
        px = sX * (n[0] + 1),
        py = sY * (n[1] + 1)
      ctx.beginPath()
      ctx.arc(px, py, baseR + rByLevel[n[2]], 0, Math.PI * 2)
      ctx.fillStyle = traceSet.has(i)
        ? `rgba(0,207,157,${aByLevel[n[2]]})`
        : `rgba(255,255,255,${aByLevel[n[2]]})`
      ctx.fill()
    }

    // 5. Glow behind each node
    for (let i = 0; i < nPx.length; i++) {
      const n = nPx[i]
      const r = 90 - n.lv * 15
      const g = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, r)
      g.addColorStop(0, traceSet.has(i) ? "rgba(0,207,157,0.10)" : "rgba(255,255,255,0.07)")
      g.addColorStop(1, "rgba(0,0,0,0)")
      ctx.fillStyle = g
      ctx.fillRect(n.x - r, n.y - r, r * 2, r * 2)
    }
    const terminal = nPx[tracePath[tracePath.length - 1]]
    const tg = ctx.createRadialGradient(terminal.x, terminal.y, 0, terminal.x, terminal.y, 140)
    tg.addColorStop(0, "rgba(0,207,157,0.18)")
    tg.addColorStop(1, "rgba(0,0,0,0)")
    ctx.fillStyle = tg
    ctx.fillRect(terminal.x - 140, terminal.y - 140, 280, 280)

    // Vignette
    const vig = ctx.createRadialGradient(W / 2, H / 2, H * 0.35, W / 2, H / 2, W * 0.55)
    vig.addColorStop(0, "rgba(0,0,0,0)")
    vig.addColorStop(1, "rgba(13,15,20,0.80)")
    ctx.fillStyle = vig
    ctx.fillRect(0, 0, W, H)
  }, [])

  return (
    <canvas ref={canvasRef} className="w-full rounded-2xl" style={{ aspectRatio: "1200/630" }} />
  )
}

/** Price feed: four-tier trust hierarchy. */
export const TrustHierarchy = () => {
  const tiers = [
    {
      tier: "Tier 1",
      name: "Stablecoins",
      count: "",
      source: "Oracle feeds + $1.00 pegs",
      examples: ["USDC", "USDT", "DAI"],
    },
    {
      tier: "Tier 2",
      name: "Spot tokens",
      count: "",
      source: "Recursive LWAP from DEX pools",
      examples: ["WETH", "UNI", "AAVE"],
    },
    {
      tier: "Tier 3",
      name: "Derivative and LP tokens",
      count: "",
      source: "Protocol-specific valuation",
      examples: ["cUSDC", "stETH", "UNI-ETH LP"],
    },
  ]

  return (
    <div className="my-6 md:my-8">
      <div className="flex flex-col gap-0">
        {tiers.map((t, i) => (
          <div key={t.tier}>
            <div className="rounded-lg border border-border-default bg-bg-popover p-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <span className="text-xs font-mono text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border border-emerald-500/25 px-2 py-0.5 rounded-sm font-medium shrink-0">
                  {t.tier}
                </span>
                <div>
                  <span className="text-sm font-medium text-fg-default">{t.name}</span>
                  {t.count && <span className="text-xs text-fg-secondary ml-2">({t.count})</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 hidden sm:flex">
                <div className="flex gap-1">
                  {t.examples.map((ex) => (
                    <span
                      key={ex}
                      className="text-[10px] font-mono text-fg-secondary bg-neutral-200 dark:bg-neutral-800 px-1.5 py-0.5 rounded-sm"
                    >
                      {ex}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            {i < tiers.length - 1 && (
              <div className="flex justify-center py-1">
                <span className="text-fg-secondary text-xs">↓</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

/** StableSwap vs Constant Product chart — single canvas with 3 curves. */

export const InteractivePriceCurves = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [info, setInfo] = useState("")
  const mouseXRef = useRef(0.5)
  const cD = 20,
    cA = 5,
    cMaxX = 28,
    cMaxY = 28,
    cPad = 48
  const cK = (cD / 2) * (cD / 2)
  const isDark = useIsDark()

  const solveSS = (x: number): number => {
    let y = cD / 2
    for (let i = 0; i < 200; i++) {
      const f = 4 * cA * (x + y) + cD - 4 * cA * cD - (cD * cD * cD) / (4 * x * y)
      const df = 4 * cA + (cD * cD * cD) / (4 * x * y * y)
      const yn = y - f / df
      if (Math.abs(yn - y) < 1e-12) break
      y = Math.max(yn, 0.00001)
    }
    return y
  }

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext("2d")
    if (!ctx) return
    const dpr = window.devicePixelRatio || 1
    const rect = canvas.getBoundingClientRect()
    canvas.width = rect.width * dpr
    canvas.height = rect.height * dpr
    ctx.scale(dpr, dpr)
    const w = rect.width,
      h = rect.height,
      gw = w - 2 * cPad,
      gh = h - 2 * cPad
    const toX = (v: number) => cPad + (v / cMaxX) * gw
    const toY = (v: number) => cPad + gh - (v / cMaxY) * gh

    ctx.clearRect(0, 0, w, h)
    const axisColor = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.15)"
    const labelColor = isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.55)"
    const tickColor = isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.45)"
    const gridColor = isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.08)"
    // Axes
    ctx.strokeStyle = axisColor
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(cPad, cPad)
    ctx.lineTo(cPad, cPad + gh)
    ctx.lineTo(cPad + gw, cPad + gh)
    ctx.stroke()
    ctx.fillStyle = labelColor
    ctx.font = "11px monospace"
    ctx.fillText("x", cPad + gw / 2, cPad + gh + 32)
    ctx.save()
    ctx.translate(16, cPad + gh / 2)
    ctx.rotate(-Math.PI / 2)
    ctx.fillText("y", 0, 0)
    ctx.restore()
    for (let v = 0; v <= 28; v += 5) {
      ctx.fillStyle = tickColor
      ctx.font = "9px monospace"
      ctx.fillText(String(v), toX(v) - 6, cPad + gh + 14)
      if (v > 0 && v <= 25) ctx.fillText(String(v), cPad - 20, toY(v) + 3)
      ctx.strokeStyle = gridColor
      ctx.beginPath()
      ctx.moveTo(toX(v), cPad)
      ctx.lineTo(toX(v), cPad + gh)
      ctx.stroke()
      if (v > 0) {
        ctx.beginPath()
        ctx.moveTo(cPad, toY(v))
        ctx.lineTo(cPad + gw, toY(v))
        ctx.stroke()
      }
    }
    // Constant sum (dotted red)
    ctx.strokeStyle = isDark ? "rgba(239,68,68,0.4)" : "rgba(220,38,38,0.5)"
    ctx.lineWidth = 1.5
    ctx.setLineDash([3, 5])
    ctx.beginPath()
    ctx.moveTo(toX(0), toY(cD))
    ctx.lineTo(toX(cD), toY(0))
    ctx.stroke()
    ctx.setLineDash([])
    // Constant product (dashed violet)
    ctx.strokeStyle = isDark ? "rgba(167,139,250,0.6)" : "rgba(124,58,237,0.55)"
    ctx.lineWidth = 1.5
    ctx.setLineDash([6, 4])
    ctx.beginPath()
    let started = false
    for (let i = 1; i <= 300; i++) {
      const x = (i / 300) * cMaxX
      const y = cK / x
      if (y > cMaxY) continue
      if (!started) {
        ctx.moveTo(toX(x), toY(y))
        started = true
      } else ctx.lineTo(toX(x), toY(y))
    }
    ctx.stroke()
    ctx.setLineDash([])
    // StableSwap (solid emerald)
    ctx.strokeStyle = isDark ? "rgba(48,217,162,0.9)" : "rgba(5,150,105,0.9)"
    ctx.lineWidth = 2.5
    ctx.beginPath()
    started = false
    for (let i = 1; i <= 500; i++) {
      const x = 0.05 + (i / 500) * (cMaxX - 0.05)
      const y = solveSS(x)
      if (y > 0 && y <= cMaxY) {
        if (!started) {
          ctx.moveTo(toX(x), toY(y))
          started = true
        } else ctx.lineTo(toX(x), toY(y))
      }
    }
    ctx.stroke()
    // Interactive dot + info on hover
    const xV = Math.max(0.5, mouseXRef.current * cMaxX)
    const yV = solveSS(xV)
    if (yV <= cMaxY) {
      ctx.beginPath()
      ctx.arc(toX(xV), toY(yV), 5, 0, Math.PI * 2)
      ctx.fillStyle = isDark ? "#30d9a2" : "#059669"
      ctx.fill()
      ctx.strokeStyle = isDark ? "rgba(48,217,162,0.3)" : "rgba(5,150,105,0.3)"
      ctx.lineWidth = 1
      ctx.setLineDash([4, 4])
      ctx.beginPath()
      ctx.moveTo(toX(xV), cPad)
      ctx.lineTo(toX(xV), cPad + gh)
      ctx.stroke()
      ctx.beginPath()
      ctx.moveTo(cPad, toY(yV))
      ctx.lineTo(cPad + gw, toY(yV))
      ctx.stroke()
      ctx.setLineDash([])
    }
    setInfo(
      `x: ${xV.toFixed(1)}   y: ${yV.toFixed(1)}   Curve (StableSwap): ${(yV / xV).toFixed(4)}   Uniswap (xy=k): ${(cK / (xV * xV)).toFixed(4)}`,
    )
  })

  const handleMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect()
    mouseXRef.current = Math.max(0.02, Math.min(0.98, (e.clientX - r.left) / r.width))
    // Force re-render to redraw canvas
    setInfo((prev) => `${prev} `)
  }

  return (
    <div className="my-6 md:my-8 rounded-xl border border-border-default bg-bg-popover p-4 md:p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none">
      <div className="text-xs font-medium text-fg-default mb-1">
        Uniswap v2 constant product vs Curve StableSwap
      </div>
      <div className="text-[10px] text-fg-secondary mb-4">
        Uniswap v2 uses the constant product invariant (xy=k). Curve StableSwap flattens the curve
        near parity so large reserve changes barely move the price. At the extremes, it steepens and
        converges toward constant product.
      </div>
      <canvas
        ref={canvasRef}
        className="w-full rounded-lg bg-bg-default cursor-crosshair"
        style={{ aspectRatio: "3/2" }}
        onMouseMove={handleMove}
      />
      <div className="text-[10px] font-mono text-fg-secondary mt-2">{info.trim()}</div>
      <div className="flex gap-4 mt-2 text-[10px] text-fg-secondary flex-wrap">
        <span className="flex items-center gap-1.5">
          <span className="w-4 h-0.5 rounded-sm bg-emerald-500" />
          Curve invariant (StableSwap)
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-4 h-0.5 rounded-sm bg-violet-400" />
          Uniswap invariant (xy=k)
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-4 h-0.5 rounded-sm bg-red-500/50 dark:bg-red-400/40" />
          Constant price (x+y=D)
        </span>
      </div>
    </div>
  )
}

export const DerivativeTokenTable = () => {
  const [time, setTime] = useState(0)
  const maxTime = 1

  /** Green or gray base bar. Violet yield stacks on top when present. Base height is fixed; yield grows above it. */
  const Bar = ({
    base,
    yield: yld,
    max,
    label,
    topLabel,
    constant,
  }: {
    base: number
    yield?: number
    max: number
    label: string
    topLabel: string
    constant?: boolean
  }) => {
    const hasYield = yld !== undefined && yld > 0.001
    // Base/constant = 45%. Yield grows above at 2x scale for visual clarity.
    const basePct = 45
    const yieldPct = hasYield ? Math.max(0, ((yld as number) / base) * basePct * 2) : 0
    const baseBg = constant
      ? "bg-neutral-300/40 dark:bg-neutral-700/40"
      : "bg-emerald-500/30 dark:bg-emerald-500/50"
    return (
      <div className="flex-1 flex flex-col items-center gap-1 h-full justify-end">
        <span
          className={`text-[10px] font-mono ${hasYield ? "text-violet-600 dark:text-violet-400" : constant ? "text-fg-secondary" : "text-emerald-600 dark:text-emerald-400"}`}
        >
          {topLabel}
        </span>
        {hasYield && (
          <div
            className="w-full rounded-t-md bg-violet-500/25 dark:bg-violet-500/40 transition-all duration-150"
            style={{ height: `${yieldPct}%` }}
          />
        )}
        <div
          className={`w-full ${hasYield ? "" : "rounded-t-md"} ${baseBg} transition-all duration-150`}
          style={{ height: `${basePct}%` }}
        />
        <span className="text-[9px] font-mono text-fg-secondary">{label}</span>
      </div>
    )
  }

  const cmpRate = 1.05 ** time
  const aaveQ = 100 * 1.05 ** time
  const lidoBal = 1.05 ** time
  const timeLeft = Math.max(0, 1 - time)
  const pt = 1 - 0.05 * timeLeft
  const yt = 0.05 * timeLeft

  return (
    <div className="my-6 md:my-8 rounded-xl border border-border-default bg-bg-popover p-4 md:p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none">
      <div className="text-xs font-medium text-fg-default mb-1">
        Each protocol accrues value differently. Quantity and rate move independently.
      </div>
      <div className="text-[10px] text-fg-secondary mb-4">
        Drag to scrub through time. Watch which component changes and which stays constant.
      </div>
      <div className="flex items-center gap-3 mb-5 px-3 py-2.5 bg-bg-default rounded-lg">
        <span className="text-[11px] font-mono text-fg-secondary shrink-0">Time</span>
        <input
          type="range"
          min={0}
          max={maxTime}
          step={0.05}
          value={time}
          onChange={(e) => setTime(parseFloat(e.target.value))}
          className="flex-1 h-1.5 rounded-lg appearance-none cursor-pointer accent-emerald-500 bg-neutral-200 dark:bg-neutral-700"
        />
        <span className="text-[13px] font-mono text-emerald-600 dark:text-emerald-400 min-w-[60px]">
          {Math.round(time * 12)}mo
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Compound: yield lives in the rate */}
        <div className="rounded-lg border border-border-default p-3">
          <div className="text-[11px] font-medium text-fg-default mb-1">Compound cToken</div>
          <div className="text-[9px] font-mono text-fg-secondary mb-2">quantity × rate</div>
          <div className="flex gap-2 h-24">
            <Bar base={100} max={150} label="Quantity" topLabel="100" constant />
            <Bar
              base={1}
              yield={cmpRate - 1}
              max={1.5}
              label="Rate"
              topLabel={`$${cmpRate.toFixed(3)}`}
            />
          </div>
          <div className="flex justify-between mt-2 pt-2 border-t border-border-default">
            <span className="text-[9px] font-mono text-fg-secondary">Total</span>
            <span className="text-[11px] font-mono text-emerald-600 dark:text-emerald-400">
              ${(100 * cmpRate).toFixed(2)}
            </span>
          </div>
        </div>
        {/* Aave: yield lives in the quantity */}
        <div className="rounded-lg border border-border-default p-3">
          <div className="text-[11px] font-medium text-fg-default mb-1">Aave aToken</div>
          <div className="text-[9px] font-mono text-fg-secondary mb-2">quantity × rate</div>
          <div className="flex gap-2 h-24">
            <Bar
              base={100}
              yield={aaveQ - 100}
              max={150}
              label="Quantity"
              topLabel={aaveQ.toFixed(1)}
            />
            <Bar base={1} max={1.5} label="Rate" topLabel="$1.00" constant />
          </div>
          <div className="flex justify-between mt-2 pt-2 border-t border-border-default">
            <span className="text-[9px] font-mono text-fg-secondary">Total</span>
            <span className="text-[11px] font-mono text-emerald-600 dark:text-emerald-400">
              ${aaveQ.toFixed(2)}
            </span>
          </div>
        </div>
        {/* Lido: yield lives in the balance */}
        <div className="rounded-lg border border-border-default p-3">
          <div className="text-[11px] font-medium text-fg-default mb-1">Lido stETH</div>
          <div className="text-[9px] font-mono text-fg-secondary mb-2">balance × price</div>
          <div className="flex gap-2 h-24">
            <Bar
              base={1}
              yield={lidoBal - 1}
              max={1.5}
              label="Balance"
              topLabel={lidoBal.toFixed(3)}
            />
            <Bar base={1} max={1.5} label="Price" topLabel="≈1 ETH" constant />
          </div>
          <div className="flex justify-between mt-2 pt-2 border-t border-border-default">
            <span className="text-[9px] font-mono text-fg-secondary">Effective</span>
            <span className="text-[11px] font-mono text-emerald-600 dark:text-emerald-400">
              {lidoBal.toFixed(3)} ETH
            </span>
          </div>
        </div>
        {/* Pendle: PT is base converging, YT is yield decaying */}
        <div className="rounded-lg border border-border-default p-3">
          <div className="text-[11px] font-medium text-fg-default mb-1">Pendle PT/YT</div>
          <div className="text-[9px] font-mono text-fg-secondary mb-2">PT + YT = underlying</div>
          <div className="flex gap-2 h-24">
            <Bar base={pt} max={1.2} label="PT" topLabel={`$${pt.toFixed(3)}`} />
            <div className="flex-1 flex flex-col items-center gap-1 h-full justify-end">
              <span className="text-[10px] font-mono text-violet-600 dark:text-violet-400">
                ${yt.toFixed(3)}
              </span>
              <div
                className="w-full rounded-t-md bg-violet-500/25 dark:bg-violet-500/40 transition-all duration-150"
                style={{ height: `${Math.max(2, (yt / 0.06) * 30)}%` }}
              />
              <span className="text-[9px] font-mono text-fg-secondary">YT</span>
            </div>
          </div>
          <div className="flex justify-between mt-2 pt-2 border-t border-border-default">
            <span className="text-[9px] font-mono text-fg-secondary">Maturity</span>
            <span className="text-[11px] font-mono text-emerald-600 dark:text-emerald-400">
              {time >= 1 ? "Matured" : `${Math.round(timeLeft * 12)}mo`}
            </span>
          </div>
        </div>
      </div>
      <div className="flex gap-4 mt-3 text-[10px] text-fg-secondary">
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-emerald-500/60" />
          Base
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-violet-500/50" />
          Yield
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-neutral-400/40 dark:bg-neutral-700/40" />
          Fixed
        </span>
      </div>
    </div>
  )
}

/** LP token price calculator: WETH-UNI example. */
export const LpTokenTable = () => {
  const [ethPrice, setEthPrice] = useState(2080)
  const uniPrice = 7.5
  // Equal-value reserves at initial prices: $5M each side
  const initWeth = 5000000 / 2080 // ~2,404 WETH
  const initUni = 5000000 / 7.5 // ~666,667 UNI
  const supply = 10000
  const k = initWeth * initUni

  // Constant product rebalancing
  const wethR = Math.sqrt((k * uniPrice) / ethPrice)
  const uniR = Math.sqrt((k * ethPrice) / uniPrice)
  const wethVal = wethR * ethPrice
  const uniVal = uniR * uniPrice
  const totalVal = wethVal + uniVal
  const lpPrice = totalVal / supply

  const fmt = (n: number) =>
    n >= 1e6 ? `${(n / 1e6).toFixed(2)}M` : n >= 1e3 ? Math.round(n).toLocaleString() : n.toFixed(2)

  return (
    <div className="my-6 md:my-8 rounded-xl border border-border-default bg-bg-popover p-4 md:p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none">
      <div className="text-xs font-medium text-fg-default mb-1">
        WETH-UNI Uniswap v2 LP token price
      </div>
      <div className="text-[10px] text-fg-secondary mb-4">
        Drag to change ETH price. Reserves rebalance along the constant product curve, and the LP
        token price updates.
      </div>
      <div className="flex items-center gap-3 mb-4 px-3 py-2.5 bg-bg-default rounded-lg">
        <span className="text-[11px] font-mono text-fg-secondary shrink-0">ETH price</span>
        <input
          type="range"
          min={500}
          max={5000}
          step={10}
          value={ethPrice}
          onChange={(e) => setEthPrice(parseFloat(e.target.value))}
          className="flex-1 h-1.5 rounded-lg appearance-none cursor-pointer accent-emerald-500 bg-neutral-200 dark:bg-neutral-700"
        />
        <span className="text-[13px] font-mono text-emerald-600 dark:text-emerald-400 min-w-[60px]">
          ${ethPrice.toLocaleString()}
        </span>
      </div>
      <div className="grid grid-cols-[auto_1fr_auto] gap-x-4 gap-y-1 font-mono text-[13px] leading-[2.2]">
        <span className="text-fg-secondary">Pool reserves</span>
        <span className="text-fg-secondary">
          {fmt(wethR)} WETH × ${ethPrice.toLocaleString()}
        </span>
        <span className="text-fg-default text-right">${fmt(wethVal)}</span>

        <span />
        <span className="text-fg-secondary">
          {fmt(uniR)} UNI × ${uniPrice.toFixed(2)}
        </span>
        <span className="text-fg-default text-right">${fmt(uniVal)}</span>

        <span />
        <span className="border-t border-border-default pt-1" />
        <span className="border-t border-border-default pt-1" />

        <span className="text-fg-secondary">Total pool value</span>
        <span />
        <span className="text-fg-default text-right">${fmt(totalVal)}</span>

        <span className="text-fg-secondary">LP supply</span>
        <span />
        <span className="text-fg-secondary text-right">{supply.toLocaleString()} tokens</span>

        <span />
        <span className="border-t border-border-default pt-1" />
        <span className="border-t border-border-default pt-1" />

        <span className="text-emerald-600 dark:text-emerald-400 font-medium">LP token price</span>
        <span />
        <span className="text-emerald-600 dark:text-emerald-400 font-medium text-right text-[15px]">
          ${lpPrice.toFixed(2)}
        </span>
      </div>
      <div className="mt-3 pt-3 border-t border-border-default text-[11px] text-fg-secondary">
        Formula:{" "}
        <span className="text-fg-default">(reserve₀ × price₀ + reserve₁ × price₁) / supply</span>
      </div>
    </div>
  )
}

/** Filtering: annotated denylist entries from production. */
export const DenylistTable = () => {
  const entries = [
    {
      chain: "ethereum",
      pool: "Curve",
      address: "0x8301...c511",
      reason: "Hacked pool, attacker drained one side",
    },
    {
      chain: "ethereum",
      pool: "Uniswap v3",
      address: "0x9663...0454",
      reason: "High total liquidity but low active liquidity",
    },
    {
      chain: "bsc",
      pool: "Apeswap",
      address: "0x5617...1e1f",
      reason: "Causes a reserve spike in aggregation",
    },
    {
      chain: "arbitrum",
      pool: "SushiSwap",
      address: "0x699c...9720",
      reason: "Causes a reserve spike in aggregation",
    },
  ]
  return (
    <div className="my-6 md:my-8 rounded-xl border border-border-default bg-bg-popover p-4 md:p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none">
      <div className="text-xs font-medium text-fg-default mb-1">
        Every excluded pool is annotated with why it was removed.
      </div>
      <div className="text-[10px] text-fg-secondary mb-4">
        The full denylist is version-controlled and auditable.
      </div>
      <div className="rounded-lg border border-border-default overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border-default">
              <th className="text-left text-xs font-medium text-fg-secondary px-4 py-3">Chain</th>
              <th className="text-left text-xs font-medium text-fg-secondary px-4 py-3">DEX</th>
              <th className="text-left text-xs font-medium text-fg-secondary px-4 py-3 hidden sm:table-cell">
                Pool
              </th>
              <th className="text-left text-xs font-medium text-fg-secondary px-4 py-3">Reason</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e, i) => (
              <tr
                key={i}
                className={i < entries.length - 1 ? "border-b border-border-default" : ""}
              >
                <td className="px-4 py-2.5 text-fg-secondary text-xs">{e.chain}</td>
                <td className="px-4 py-2.5 text-xs">
                  <span className="px-1.5 py-0.5 rounded-sm text-[10px] font-mono bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20">
                    {e.pool}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-fg-secondary text-xs font-mono hidden sm:table-cell">
                  {e.address}
                </td>
                <td className="px-4 py-2.5 text-fg-default text-xs">{e.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/** Filtering: price spike rejection visualization. */
export const SpikeRejection = () => {
  const isDark = useIsDark()
  // Simulated hourly prices with a manipulation spike at hour 8
  const raw = [
    1.0, 1.02, 0.98, 1.01, 1.03, 0.99, 1.0, 1.02, 14.5, 1.01, 0.97, 1.02, 1.0, 0.98, 1.01, 1.03,
    0.99, 1.01, 1.0, 0.98, 1.02, 1.01, 0.99, 1.0,
  ]
  const cleaned = raw.map((p, i) => {
    if (i === 0) return p
    const prev = raw[i - 1]
    const next = i < raw.length - 1 ? raw[i + 1] : null
    if (p > prev * 10 || p < prev * 0.1) {
      if (next !== null && (p < next * 0.5 || p > next * 2)) return null // rejected
    }
    return p
  })

  const pad = 40
  const w = 600,
    h = 160
  const gw = w - 2 * pad,
    gh = h - 2 * pad
  const maxP = 16,
    minP = 0

  const toX = (i: number) => pad + (i / (raw.length - 1)) * gw
  const toY = (p: number) => pad + gh - ((p - minP) / (maxP - minP)) * gh

  // Raw line path
  const rawPath = raw
    .map((p, i) => `${i === 0 ? "M" : "L"}${toX(i).toFixed(1)},${toY(p).toFixed(1)}`)
    .join(" ")

  // Cleaned line path (skip nulls)
  const cleanedSegments: string[] = []
  let started = false
  cleaned.forEach((p, i) => {
    if (p === null) {
      started = false
      return
    }
    cleanedSegments.push(`${!started ? "M" : "L"}${toX(i).toFixed(1)},${toY(p).toFixed(1)}`)
    started = true
  })
  const cleanedPath = cleanedSegments.join(" ")

  // 10x threshold lines from the pre-spike price
  const preSpike = raw[7] // 1.02
  const thresholdY = toY(preSpike * 10) // 10.2

  return (
    <div className="my-6 md:my-8 rounded-xl border border-border-default bg-bg-popover p-4 md:p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none">
      <div className="text-xs font-medium text-fg-default mb-1">
        A price that jumps more than 10x from the previous hour is rejected unless the next hour
        confirms it.
      </div>
      <div className="text-[10px] text-fg-secondary mb-4">
        The spike at hour 8 reverts immediately, so it is flagged as manipulation and removed from
        the feed.
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ maxHeight: 180 }}>
        {/* Grid */}
        <line
          x1={pad}
          y1={pad}
          x2={pad}
          y2={pad + gh}
          stroke={isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.15)"}
        />
        <line
          x1={pad}
          y1={pad + gh}
          x2={pad + gw}
          y2={pad + gh}
          stroke={isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.15)"}
        />
        {/* 10x threshold */}
        <line
          x1={pad}
          y1={thresholdY}
          x2={pad + gw}
          y2={thresholdY}
          stroke={isDark ? "rgba(239,68,68,0.2)" : "rgba(239,68,68,0.35)"}
          strokeDasharray="4 4"
        />
        <text
          x={pad + gw - 2}
          y={thresholdY - 4}
          fill={isDark ? "rgba(239,68,68,0.4)" : "rgba(220,38,38,0.7)"}
          fontSize="9"
          fontFamily="monospace"
          textAnchor="end"
        >
          10x threshold
        </text>
        {/* $1 reference */}
        <line
          x1={pad}
          y1={toY(1)}
          x2={pad + gw}
          y2={toY(1)}
          stroke={isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.15)"}
          strokeDasharray="2 4"
        />
        <text
          x={pad - 4}
          y={toY(1) + 3}
          fill={isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.55)"}
          fontSize="9"
          fontFamily="monospace"
          textAnchor="end"
        >
          $1
        </text>
        {/* Raw line (faded) */}
        <path
          d={rawPath}
          fill="none"
          stroke={isDark ? "rgba(239,68,68,0.3)" : "rgba(239,68,68,0.4)"}
          strokeWidth="1.5"
        />
        {/* Spike dot */}
        <circle
          cx={toX(8)}
          cy={toY(14.5)}
          r="4"
          fill={isDark ? "rgba(239,68,68,0.6)" : "rgba(220,38,38,0.7)"}
        />
        <text
          x={toX(8) + 8}
          y={toY(14.5) + 3}
          fill={isDark ? "rgba(239,68,68,0.6)" : "rgba(220,38,38,0.8)"}
          fontSize="9"
          fontFamily="monospace"
        >
          $14.50 · rejected
        </text>
        {/* Cleaned line */}
        <path
          d={cleanedPath}
          fill="none"
          stroke={isDark ? "rgba(48,217,162,0.8)" : "rgba(5,150,105,0.8)"}
          strokeWidth="2"
        />
        {/* Hour labels */}
        {[0, 6, 12, 18, 23].map((i) => (
          <text
            key={i}
            x={toX(i)}
            y={pad + gh + 14}
            fill={isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.55)"}
            fontSize="9"
            fontFamily="monospace"
            textAnchor="middle"
          >
            h{i}
          </text>
        ))}
      </svg>
      <div className="flex gap-4 mt-2 text-[10px] text-fg-secondary">
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-emerald-500" />
          Cleaned price
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-red-500/60" />
          Rejected spike
        </span>
      </div>
    </div>
  )
}

/** Filtering: sender count distribution with threshold. */
export const TradingFilterScatter = () => {
  const isDark = useIsDark()
  // Simulated tokens: most spam (low senders), some legitimate, PEPE as exception
  const tokens: {
    name: string
    senders: number
    volume: number
    status: "pass" | "filtered" | "exception"
  }[] = [
    { name: "Token A", senders: 3, volume: 120, status: "filtered" },
    { name: "Token B", senders: 8, volume: 45, status: "filtered" },
    { name: "Token C", senders: 12, volume: 200, status: "filtered" },
    { name: "Token D", senders: 22, volume: 80, status: "filtered" },
    { name: "Token E", senders: 35, volume: 150, status: "filtered" },
    { name: "Token F", senders: 41, volume: 300, status: "filtered" },
    { name: "UNI", senders: 180, volume: 500, status: "pass" },
    { name: "AAVE", senders: 95, volume: 350, status: "pass" },
    { name: "CRV", senders: 120, volume: 420, status: "pass" },
    { name: "LINK", senders: 210, volume: 600, status: "pass" },
    { name: "WBTC", senders: 160, volume: 700, status: "pass" },
    { name: "PEPE", senders: 250, volume: 950, status: "exception" },
  ]

  const pad = 44
  const w = 600,
    h = 200
  const gw = w - 2 * pad,
    gh = h - 2 * pad
  const maxSenders = 280,
    maxVolume = 1000
  const threshold = 50

  const toX = (s: number) => pad + (s / maxSenders) * gw
  const toY = (v: number) => pad + gh - (v / maxVolume) * gh

  return (
    <div className="my-6 md:my-8 rounded-xl border border-border-default bg-bg-popover p-4 md:p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none">
      <div className="text-xs font-medium text-fg-default mb-1">
        Tokens below the sender threshold are filtered as likely spam, regardless of pool liquidity.
      </div>
      <div className="text-[10px] text-fg-secondary mb-4">
        PEPE is allowlisted past the large-trade filter despite extreme token quantities per swap.
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ maxHeight: 220 }}>
        {/* Axes */}
        <line
          x1={pad}
          y1={pad}
          x2={pad}
          y2={pad + gh}
          stroke={isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.15)"}
        />
        <line
          x1={pad}
          y1={pad + gh}
          x2={pad + gw}
          y2={pad + gh}
          stroke={isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.15)"}
        />
        {/* Threshold line */}
        <line
          x1={toX(threshold)}
          y1={pad}
          x2={toX(threshold)}
          y2={pad + gh}
          stroke={isDark ? "rgba(239,68,68,0.3)" : "rgba(239,68,68,0.4)"}
          strokeDasharray="4 4"
        />
        <text
          x={toX(threshold)}
          y={pad - 6}
          fill={isDark ? "rgba(239,68,68,0.5)" : "rgba(239,68,68,0.7)"}
          fontSize="9"
          fontFamily="monospace"
          textAnchor="middle"
        >
          min senders
        </text>
        {/* Filtered zone */}
        <rect
          x={pad}
          y={pad}
          width={toX(threshold) - pad}
          height={gh}
          fill={isDark ? "rgba(239,68,68,0.03)" : "rgba(239,68,68,0.06)"}
        />
        {/* Dots */}
        {tokens.map((t) => (
          <g key={t.name}>
            <circle
              cx={toX(t.senders)}
              cy={toY(t.volume)}
              r={t.status === "exception" ? 6 : 4}
              fill={
                t.status === "filtered"
                  ? isDark
                    ? "rgba(255,255,255,0.12)"
                    : "rgba(0,0,0,0.25)"
                  : t.status === "exception"
                    ? isDark
                      ? "rgba(251,191,36,0.7)"
                      : "rgba(217,158,0,0.8)"
                    : isDark
                      ? "rgba(48,217,162,0.6)"
                      : "rgba(5,150,105,0.7)"
              }
            />
            {(t.status === "pass" || t.status === "exception") && (
              <text
                x={toX(t.senders) + 8}
                y={toY(t.volume) + 3}
                fill={
                  t.status === "exception"
                    ? isDark
                      ? "rgba(251,191,36,0.7)"
                      : "rgba(180,130,0,0.9)"
                    : isDark
                      ? "rgba(48,217,162,0.5)"
                      : "rgba(5,150,105,0.8)"
                }
                fontSize="9"
                fontFamily="monospace"
              >
                {t.name}
              </text>
            )}
          </g>
        ))}
        {/* Axis labels */}
        <text
          x={pad + gw / 2}
          y={pad + gh + 16}
          fill={isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.55)"}
          fontSize="9"
          fontFamily="monospace"
          textAnchor="middle"
        >
          Unique senders
        </text>
        <text
          x={pad - 8}
          y={pad + gh / 2}
          fill={isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.55)"}
          fontSize="9"
          fontFamily="monospace"
          textAnchor="middle"
          transform={`rotate(-90, ${pad - 8}, ${pad + gh / 2})`}
        >
          Volume
        </text>
      </svg>
      <div className="flex gap-4 mt-2 text-[10px] text-fg-secondary">
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-emerald-500" />
          Passes
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-neutral-400 dark:bg-neutral-600" />
          Filtered
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-amber-400" />
          Allowlisted exception
        </span>
      </div>
    </div>
  )
}

/** Coverage dashboard showing automation progress across capability layers. */
/** Uniswap v3/v4 tick-based liquidity chart. */
export const TickChart = () => {
  const isDark = useIsDark()
  const ticks = [
    { range: "1,700", liq: 0.3, side: "pink" as const },
    { range: "1,750", liq: 0.5, side: "pink" as const },
    { range: "1,800", liq: 0.8, side: "pink" as const },
    { range: "1,850", liq: 1.4, side: "pink" as const },
    { range: "1,900", liq: 2.8, side: "pink" as const },
    { range: "1,950", liq: 4.2, side: "pink" as const },
    { range: "2,000", liq: 6.1, side: "pink" as const },
    { range: "2,050", liq: 7.8, side: "pink" as const },
    { range: "2,080", liq: 8.3, side: "active" as const },
    { range: "2,100", liq: 7.2, side: "blue" as const },
    { range: "2,150", liq: 5.6, side: "blue" as const },
    { range: "2,200", liq: 4.1, side: "blue" as const },
    { range: "2,250", liq: 2.9, side: "blue" as const },
    { range: "2,300", liq: 1.8, side: "blue" as const },
    { range: "2,350", liq: 1.0, side: "blue" as const },
    { range: "2,400", liq: 0.6, side: "blue" as const },
    { range: "2,450", liq: 0.3, side: "blue" as const },
  ]
  const maxLiq = Math.max(...ticks.map((t) => t.liq))
  const showLabel = (r: string) => ["1,700", "1,900", "2,080", "2,200", "2,450"].includes(r)

  return (
    <div className="my-6 md:my-8 rounded-xl border border-border-default bg-bg-popover p-4 md:p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none">
      <div className="text-xs font-medium text-fg-default mb-1">
        Uniswap v3/v4: liquidity is concentrated in tick ranges. Each tick has its own price.
      </div>
      <div className="text-[10px] text-fg-secondary mb-4">
        The active tick determines which liquidity is used for the next trade. Taller bars mean
        deeper liquidity at that price.
      </div>
      <div className="flex items-end gap-[3px]" style={{ height: 180, padding: "0 4px" }}>
        {ticks.map((t) => {
          const hPct = Math.max(3, (t.liq / maxLiq) * 90)
          const bg =
            t.side === "active"
              ? isDark
                ? "rgba(48,217,162,0.7)"
                : "rgba(5,150,105,0.6)"
              : t.side === "pink"
                ? isDark
                  ? "rgba(236,72,153,0.25)"
                  : "rgba(236,72,153,0.35)"
                : isDark
                  ? "rgba(96,165,250,0.25)"
                  : "rgba(59,130,246,0.3)"
          const border =
            t.side === "active"
              ? isDark
                ? "2px solid rgba(48,217,162,0.9)"
                : "2px solid rgba(5,150,105,0.8)"
              : t.side === "pink"
                ? isDark
                  ? "1px solid rgba(236,72,153,0.3)"
                  : "1px solid rgba(236,72,153,0.45)"
                : isDark
                  ? "1px solid rgba(96,165,250,0.3)"
                  : "1px solid rgba(59,130,246,0.4)"
          return (
            <div
              key={t.range}
              className="flex-1 flex flex-col items-center justify-end h-full gap-[2px]"
            >
              {t.side === "active" ? (
                <span className="text-[8px] font-mono text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border border-emerald-500/25 px-1 py-px rounded-sm">
                  active
                </span>
              ) : (
                <span className="text-[8px] font-mono text-fg-secondary">${t.liq}M</span>
              )}
              <div
                className="w-full rounded-t"
                style={{ height: `${hPct}%`, background: bg, border }}
              />
            </div>
          )
        })}
      </div>
      <div className="flex gap-[3px] mt-1" style={{ padding: "0 4px" }}>
        {ticks.map((t) => (
          <div
            key={t.range}
            className="flex-1 text-center"
            style={{
              fontSize: showLabel(t.range) ? 9 : 0,
              fontFamily: "var(--font-mono, monospace)",
              color:
                t.side === "active"
                  ? isDark
                    ? "#30d9a2"
                    : "#059669"
                  : "var(--text-secondary, #737373)",
              fontWeight: t.side === "active" ? 500 : 400,
            }}
          >
            {showLabel(t.range) ? `$${t.range}` : ""}
          </div>
        ))}
      </div>
      <div className="flex justify-between mt-2 px-1">
        <span className="text-[9px] font-mono text-fg-secondary">1 WETH = $2,080 USDC</span>
        <span className="text-[9px] font-mono text-fg-secondary">Each bar = one tick range</span>
      </div>
      <div className="flex gap-4 mt-2 text-[10px] text-fg-secondary">
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-pink-500/50" />
          Token A side
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-blue-400/50" />
          Token B side
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-emerald-500/70" />
          Active tick
        </span>
      </div>
    </div>
  )
}

export const CoverageDashboard = () => {
  const layers = [
    {
      name: "Chain integration",
      covered: 6,
      partial: 1,
      total: 8,
      status: "Agents run most of this end to end",
    },
    {
      name: "Metric definition",
      covered: 5,
      partial: 1,
      total: 6,
      status: "Was zero coverage a month ago",
    },
    {
      name: "App integration",
      covered: 8,
      partial: 2,
      total: 11,
      status: "The most steps and highest coverage",
    },
    {
      name: "Serving layer",
      covered: 1,
      partial: 3,
      total: 5,
      status: "Largest gap on the map",
    },
    {
      name: "Research & distribution",
      covered: 1,
      partial: 1,
      total: 4,
      status: "Earliest stage",
    },
  ]

  return (
    <div className="my-6 md:my-8 rounded-lg border border-border-default bg-bg-popover p-5 md:p-6">
      <div className="flex flex-col gap-5">
        {layers.map((layer) => (
          <div key={layer.name}>
            <div className="flex justify-between items-baseline mb-2">
              <span className="text-sm font-medium text-fg-default">{layer.name}</span>
              <span className="text-xs text-fg-secondary tabular-nums">
                {layer.covered} / {layer.total}
              </span>
            </div>
            <div className="h-1.5 bg-neutral-200 dark:bg-neutral-800 rounded-full overflow-hidden flex">
              <div
                className="bg-emerald-500 h-full"
                style={{ width: `${(layer.covered / layer.total) * 100}%` }}
              />
              <div
                className="bg-amber-500 h-full"
                style={{ width: `${(layer.partial / layer.total) * 100}%` }}
              />
            </div>
            <div className="text-xs text-fg-secondary mt-1.5">{layer.status}</div>
          </div>
        ))}
      </div>
      <div className="flex gap-4 mt-5 pt-4 border-t border-border-default text-xs text-fg-secondary">
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-emerald-500" />
          Covered
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-amber-500" />
          Partial
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-neutral-400 dark:bg-neutral-700" />
          Missing
        </span>
      </div>
    </div>
  )
}

/** Viz: Principle doc — excerpt from a real .md file with ... indicating more above/below. */
export const PrincipleDocCard = () => {
  const lines = [
    { text: "## DECISION", style: "text-emerald-500 font-medium text-[11px] tracking-wider" },
    { text: "", style: "" },
    {
      text: "Extract the marginal price -- the exchange rate for an",
      style: "text-fg-secondary",
    },
    {
      text: "infinitesimally small trade at the current reserve point",
      style: "text-fg-secondary",
    },
    {
      text: "-- not the execution price of any specific trade. Each",
      style: "text-fg-secondary",
    },
    {
      text: "invariant family has its own derivation, ranging from a",
      style: "text-fg-secondary",
    },
    {
      text: "single division (constant product) to an RPC call (Curve V2).",
      style: "text-fg-secondary",
    },
    { text: "", style: "" },
    { text: "## WHY", style: "text-emerald-500 font-medium text-[11px] tracking-wider" },
    { text: "", style: "" },
    {
      text: "Marginal price represents pool state independent of any",
      style: "text-fg-secondary",
    },
    {
      text: "particular trade size. It is comparable across pools,",
      style: "text-fg-secondary",
    },
    {
      text: "stable between trades, and the correct input for",
      style: "text-fg-secondary",
    },
    {
      text: "downstream LWAP aggregation.",
      style: "text-fg-secondary",
    },
    { text: "", style: "" },
    { text: "...", style: "text-neutral-600" },
    { text: "", style: "" },
    {
      text: "## REJECTED ALTERNATIVE",
      style: "text-red-400 font-medium text-[11px] tracking-wider",
    },
    { text: "", style: "" },
    {
      text: "Extract execution price from Swap events (amount_in /",
      style: "text-fg-secondary",
    },
    {
      text: "amount_out per trade). Rejected because the price depends",
      style: "text-fg-secondary",
    },
    {
      text: "on trade size, making it non-comparable across pools and",
      style: "text-fg-secondary",
    },
    {
      text: "trades. A $100 swap and a $1M swap in the same pool yield",
      style: "text-fg-secondary",
    },
    {
      text: "different prices.",
      style: "text-fg-secondary",
    },
    { text: "", style: "" },
    { text: "## TRADEOFF", style: "text-amber-500 font-medium text-[11px] tracking-wider" },
    { text: "", style: "" },
    {
      text: "The marginal price derivation differs per invariant",
      style: "text-fg-secondary",
    },
    {
      text: "family, creating per-DEX complexity in the pipeline.",
      style: "text-fg-secondary",
    },
    { text: "", style: "" },
    { text: "...", style: "text-neutral-600" },
    { text: "", style: "" },
    {
      text: "[[Constant product (Uniswap V2)]]  [[Concentrated liquidity (Uniswap V3)]]",
      style: "text-emerald-600 dark:text-emerald-400",
    },
    {
      text: "[[Weighted pools (Balancer)]]  [[StableSwap (Curve V1)]]  ...",
      style: "text-emerald-600 dark:text-emerald-400",
    },
  ]

  return (
    <div className="my-6 md:my-8">
      <div className="rounded-lg border border-border-default bg-neutral-950 overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border-default bg-neutral-900/60">
          <span className="text-[11px] font-mono text-fg-secondary">
            Extract marginal price from pool state.md
          </span>
        </div>
        <div className="p-4 font-mono text-[12px] leading-[1.7]">
          {lines.map((line, i) => (
            <div key={i} className={line.style || "h-4"}>
              {line.text}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/** Viz: Folder structure — redacted directory tree showing depth without full blueprint. */
export const KnowledgeGraph = () => {
  type Entry = {
    name: string
    indent: number
    highlight?: boolean
    folder?: boolean
    dots?: boolean
  }
  const tree: Entry[] = [
    { name: "context/", indent: 0, folder: true },
    { name: "...", indent: 1, dots: true },
    { name: "Prices/", indent: 1, folder: true },
    { name: "Pool pricing/", indent: 2, folder: true },
    { name: "Decisions/", indent: 3, folder: true },
    { name: "Compute locally, state-call for hidden-state invariants.md", indent: 4 },
    { name: "Decompose multi-token pools into 2-token pairs.md", indent: 4 },
    { name: "Extract marginal price from pool state.md", indent: 4, highlight: true },
    { name: "Match each DEX to one invariant family.md", indent: 4 },
    { name: "Pool types/", indent: 3, folder: true },
    { name: "Concentrated liquidity (Uniswap V3).md", indent: 4 },
    { name: "Constant product (Uniswap V2).md", indent: 4 },
    { name: "Crypto pools (Curve V2).md", indent: 4 },
    { name: "Solidly stable (Velodrome).md", indent: 4 },
    { name: "StableSwap (Curve V1).md", indent: 4 },
    { name: "Weighted pools (Balancer).md", indent: 4 },
    { name: "Pool pricing.md", indent: 3 },
    { name: "Prices aggregation/", indent: 2, folder: true },
    { name: "Decisions/", indent: 3, folder: true },
    { name: "Aggregate prices hourly with forward-fill.md", indent: 4 },
    { name: "Apply defense-in-depth filtering at pool, cycle, aggregation.md", indent: 4 },
    { name: "Derive prices from pool reserves at block boundaries.md", indent: 4 },
    { name: "...", indent: 4, dots: true },
    { name: "Prices aggregation.md", indent: 3 },
    { name: "State/", indent: 1, folder: true },
    { name: "...", indent: 2, dots: true },
    { name: "Labels/", indent: 1, folder: true },
    { name: "...", indent: 2, dots: true },
    { name: "Abstractions/", indent: 1, folder: true },
    { name: "...", indent: 2, dots: true },
    { name: "...", indent: 1, dots: true },
  ]

  return (
    <div className="my-6 md:my-8">
      <div className="rounded-lg border border-border-default bg-neutral-950 overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border-default bg-neutral-900/60">
          <span className="text-[11px] font-mono text-fg-secondary">context</span>
        </div>
        <div className="p-4 font-mono text-[12px] leading-[1.8]">
          {tree.map((entry, i) => (
            <div
              key={i}
              style={{ paddingLeft: entry.indent * 16 }}
              className={
                entry.highlight
                  ? "text-emerald-400"
                  : entry.dots
                    ? "text-neutral-600"
                    : entry.folder
                      ? "text-fg-default"
                      : "text-fg-secondary"
              }
            >
              {entry.dots
                ? "..."
                : entry.folder
                  ? `📁 ${entry.name}`
                  : `${entry.highlight ? "→ " : ""}${entry.name}`}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/** Asset pipeline diagram: standard path + override path → one output. */
export const AssetPipelineDiagram = () => {
  const overrides = [
    { issuer: "Circle", event: "Mint(minter, to, amount)" },
    { issuer: "Tether", event: "Issue(amount)" },
    { issuer: "Paxos", event: "SupplyIncreased(to, value)" },
  ]
  const outputColumns = ["timestamp", "asset_id", "chain_id", "metric_value"]

  return (
    <div className="my-6 md:my-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Standard path */}
        <div className="rounded-lg border border-border-default bg-bg-popover p-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-mono text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border border-emerald-500/25 px-2 py-0.5 rounded-sm font-medium">
              ~80%
            </span>
            <span className="text-sm font-medium text-fg-default">Standard path</span>
          </div>
          <div className="text-xs text-fg-secondary mb-2">One event across 50 chains</div>
          <div className="text-[11px] font-mono text-fg-secondary bg-neutral-200 dark:bg-neutral-800 px-2 py-1 rounded-sm inline-block">
            Transfer(from, to, value)
          </div>
        </div>

        {/* Override path */}
        <div className="rounded-lg border border-border-default bg-bg-popover p-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-mono text-amber-600 dark:text-amber-400 bg-amber-500/10 border border-amber-500/25 px-2 py-0.5 rounded-sm font-medium">
              ~20%
            </span>
            <span className="text-sm font-medium text-fg-default">Override path</span>
          </div>
          <div className="text-xs text-fg-secondary mb-3">Issuer-specific contract events</div>
          <div className="flex flex-col gap-1.5">
            {overrides.map((o) => (
              <div key={o.issuer} className="flex items-center gap-2">
                <span className="text-[10px] font-mono text-fg-secondary bg-neutral-200 dark:bg-neutral-800 px-1.5 py-0.5 rounded-sm">
                  {o.issuer}
                </span>
                <span className="text-[10px] text-fg-secondary opacity-50">→</span>
                <span className="text-[10px] font-mono text-fg-secondary opacity-70">
                  {o.event}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Merge arrow */}
      <div className="flex justify-center py-2">
        <span className="text-fg-secondary text-xs">↓</span>
      </div>

      {/* Output */}
      <div className="rounded-lg border border-emerald-500/25 bg-emerald-500/5 p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-sm font-medium text-fg-default">One output table</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {outputColumns.map((col) => (
            <span
              key={col}
              className="text-[10px] font-mono text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border border-emerald-500/25 px-1.5 py-0.5 rounded-sm"
            >
              {col}
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}
